import { useEffect, useRef, useState } from "react";
import moment from "moment-timezone";
import Image from "next/image";
import { TYPO } from "styles/Typo";
import { useHomeNewsRader } from "utils/clientAPIs/newsRader";
import { useHandleImageErrorVer2 } from "utils/useHandleImageError";
import NewLaderDetailModal from "./NewLaderDetailModal";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Mousewheel } from "swiper/modules";
import "swiper/css";
import { useRouter } from "next/router";
import { useTranslation } from "next-i18next";
import type { Swiper as SwiperType } from "swiper";
import SkeletonBlock from "components/skeleton";
import { TrackedModalProvider } from "lib/analytics/react/modal";
import { TrackedNewsRaderLink } from "lib/analytics/react/link";

function MainNewLeader() {
  const { data, isLoading, isError } = useHomeNewsRader();
  const [open, setOpen] = useState(false);
  const { getStockLogoSafe } = useHandleImageErrorVer2();
  const [containerHeight, setContainerHeight] = useState<number | undefined>(
    undefined
  );
  const { t } = useTranslation("main");
  const router = useRouter();
  const formatNewsDate = (d: string) =>
    router.locale === "ko"
      ? moment(d).format("M월 D일")
      : moment(d).format("MMM D");
  useEffect(() => {
    if (isLoading || isError || data?.length === 0) return;
    const measure = () => {
      if (!listRef.current) return;
      const nodes = listRef.current.querySelectorAll<HTMLElement>(".each-news");
      if (!nodes || nodes.length === 0) return;
      const first = nodes[0]?.getBoundingClientRect().height || 0;
      const second = nodes[1]?.getBoundingClientRect().height || 0;
      const computed = Math.ceil(first + second + 50);
      setContainerHeight(computed > 0 ? computed : 245); // 최소 높이 300px 보장
    };

    // 레이아웃이 완전히 잡힌 후 측정
    const raf = requestAnimationFrame(() => {
      setTimeout(measure, 100); // Swiper 마운트 후 측정
    });
    window.addEventListener("resize", measure);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", measure);
    };
  }, [data]);

  const listRef = useRef<HTMLDivElement>(null);
  const firstRunRef = useRef(true);
  const swiperRef = useRef<SwiperType | null>(null);
  if (isLoading)
    return (
      <div className="px-6 bg-white">
        <SkeletonBlock width={"100%"} height={265} />
      </div>
    );
  if (isError) return <div></div>;

  if (data?.length === 0) return null;

  {
    /** TODO : HEIGHT 고정 x, 세번째 뉴스 살짝 보이는 정도로 변경 */
  }

  if (!data) return null;
  const shouldLoop = (data?.length ?? 0) > 3;
  return (
    <div className="bg-white">
      <h2 className={`${TYPO.TYPO4_SB} text-gray80 pt-6 px-6`}>
        {t("latestStockNews")}
      </h2>
      <div className="py-4 px-5 bg-white">
        <div
          className="rounded-[20px] overflow-hidden flex flex-col border border-hairlineBorder relative"
          style={{ height: `${containerHeight || 245}px` }}
        >
          <div className="mx-4  h-full">
            <div className="h-full" ref={listRef}>
              <Swiper
                direction="vertical"
                modules={[Autoplay, Mousewheel]}
                loop={shouldLoop}
                onInit={(swiper: SwiperType) => {
                  swiperRef.current = swiper;
                  setTimeout(() => {
                    swiper.slideNext(600); // 한 번 빠르게 이동
                    if (
                      swiper?.params &&
                      swiper?.params?.autoplay &&
                      typeof swiper?.params?.autoplay === "object"
                    ) {
                      swiper.params.autoplay.delay = 2500; // 일반 딜레이
                      swiper.params.autoplay.disableOnInteraction = false;
                      swiper.params.autoplay.pauseOnMouseEnter = true;
                    }
                    // autoplay가 존재하는지 확인 후 시작
                    if (
                      swiper?.autoplay &&
                      typeof swiper?.autoplay?.start === "function"
                    ) {
                      swiper.autoplay.start();
                    }
                  }, 700); // 첫 대기시간
                }}
                autoplay={false}
                slidesPerView="auto"
                spaceBetween={0}
                mousewheel={false}
                allowTouchMove={false}
                speed={600} // 스크롤 속도
                className="h-full"
                centeredSlides={false}
                loopAdditionalSlides={3}
                style={{
                  padding: "16px 0 0 0",
                }}
              >
                {data?.map((item: any, index: number) => (
                  <SwiperSlide key={index} className="!h-auto">
                    <TrackedNewsRaderLink
                      href={`/detail/${item.ITEM_CD_DL}?tab=aireport&sub=news`}
                      ticker={item?.ITEM_CD_DL}
                      newsraderId={item?.ID}
                      fromListId="explore_latest_news"
                      rank={index + 1}
                      asTrace={true}
                      className="each-news cursor-pointer block"
                    >
                      <div className="flex items-stretch ">
                        <div className="w-[52px] flex flex-col  shrink-0 items-end">
                          <p
                            className={`text-gray60 ${TYPO.TYPO8_RG} whitespace-nowrap`}
                          >
                            {formatNewsDate(item.BASE_DT)}
                          </p>
                          <p
                            className={`${TYPO.TYPO8_RG} ${
                              Number(item?.ADJ_CLOSE_CHG?.toFixed(1)) === 0
                                ? "text-gray70"
                                : item?.ADJ_CLOSE_CHG > 0
                                ? "text-red40"
                                : "text-blue40"
                            }`}
                          >
                            {Number(item?.ADJ_CLOSE_CHG?.toFixed(1)) > 0
                              ? "+"
                              : ""}
                            {item?.ADJ_CLOSE_CHG?.toFixed(1)}%
                          </p>
                        </div>
                        <div className="px-4">
                          <div
                            className="h-full w-[1px]  relative"
                            style={{
                              background:
                                // index === 0
                                //   ? "linear-gradient(180deg, rgba(229, 232, 235, 0.00) 0%, #E5E8EB 100%)"
                                //   : index === data?.slice(0, 3)?.length - 1
                                //   ? "linear-gradient(180deg, #E5E8EB 0%, rgba(229, 232, 235, 0.00) 100%)"
                                //   :
                                "var(--hairlineBorder)",
                            }}
                          />
                        </div>
                        <div className="flex flex-col gap-1 pb-[30px]">
                          <div className="flex gap-1 items-center">
                            <Image
                              src={getStockLogoSafe(item.ITEM_CD_DL)}
                              alt={item.ITEM_CD_DL}
                              width={18}
                              height={18}
                              className="rounded-[6px]"
                            />
                            <p
                              className={`${TYPO.TYPO8_MD} text-gray70 leading-[20px]`}
                            >
                              {item.ITEM_KR_NM}
                            </p>
                          </div>
                          <p
                            className={`${TYPO.TYPO7_1_MD} text-gray80 whitespace-pre-line`}
                          >
                            {item?.KEYWORD?.split(",").join("\n")}
                          </p>
                          {/* <p
                          className={`${TYPO.TYPO8_RG} text-gray60 line-clamp-1`}
                        >
                          {item.DESCRIPTION}
                        </p> */}
                        </div>
                      </div>
                    </TrackedNewsRaderLink>
                  </SwiperSlide>
                ))}
              </Swiper>
              <style jsx global>{`
                .swiper-vertical > .swiper-wrapper > .swiper-slide {
                  height: auto !important;
                }
              `}</style>
            </div>
          </div>
          <div
            className="w-full h-[64px] z-[1] absolute bottom-0 left-0"
            style={{
              background:
                "linear-gradient(180deg, rgba(255, 255, 255, 0.00) 0%, var(--background, #FFF) 100%)",
            }}
          />
          {data?.length > 2 && (
            <div
              className="cursor-pointer z-[2] absolute bottom-[10px] left-[50%] -translate-x-1/2 px-3 py-[6px] flex gap-2 items-center bg-floatBackground border border-hairlineBorder rounded-[30px]"
              onClick={() => setOpen(true)}
              data-testid="new-leader-more-button"
            >
              <p className={`${TYPO.TYPO7_1_MD} text-gray70`}>{t("seeMore")}</p>
              <Arrow />
            </div>
          )}
        </div>
        <TrackedModalProvider
          modalId="news_rader_detail_modal"
          isOpen={open}
          triggerSectionId="main_news_section"
          triggerType="user_click"
        >
          {open && (
            <NewLaderDetailModal setModalOpen={setOpen} isModalOpen={open} />
          )}
        </TrackedModalProvider>
      </div>
    </div>
  );
}

const Arrow = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="12"
    height="12"
    viewBox="0 0 12 12"
    fill="none"
  >
    <path
      d="M6.02911 7.6375L9.70411 3.9625C9.82911 3.8375 9.97494 3.77708 10.1416 3.78125C10.3083 3.78542 10.4541 3.85 10.5791 3.975C10.7041 4.1 10.7666 4.24583 10.7666 4.4125C10.7666 4.57917 10.7041 4.725 10.5791 4.85L6.74161 8.7C6.64161 8.8 6.52911 8.875 6.40411 8.925C6.27911 8.975 6.15411 9 6.02911 9C5.90411 9 5.77911 8.975 5.65411 8.925C5.52911 8.875 5.41661 8.8 5.31661 8.7L1.46661 4.85C1.34161 4.725 1.28119 4.57708 1.28536 4.40625C1.28952 4.23542 1.35411 4.0875 1.47911 3.9625C1.60411 3.8375 1.74994 3.775 1.91661 3.775C2.08327 3.775 2.22911 3.8375 2.35411 3.9625L6.02911 7.6375Z"
      fill="var(--gray40)"
    />
  </svg>
);

export default MainNewLeader;
